import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, 
          Button, 
          CircularProgress, 
          IconButton, 
           } from '@mui/material';



import ContentPanel from './ContentPanel';
import VoiceRecorder from './VoiceRecorder';
import { Sentence } from './models/Sentence';
import { useConfig } from './ConfigHolder';
import { useSwipeable } from 'react-swipeable';
import { isEmpty } from 'lodash';
import FirstTimeLangSelection from './FirstTimeLangSelection';
import ErrorDispatcher from './ErrorDispatcher';
import Footer from './Footer';
import Header from './Header';
import { useAutoPlay } from './contexts/AutoPlayContext';
import {BasicEvents} from './fsm/AutoPlayBaseFSM';
import { ReactComponent as JustPlayStopIcon } from "./resources/JustPlayStop.svg";

// import { AutoPlayConfig, AutoPlayModes } from './components/AutoPlayConstants';

const MainContent = () => {
  const [sentence, setSentence] = useState(new Sentence());
  const [sentenceSource, setSentenceSource] = useState(null); // where the sentence was selected from
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(1);
  // const [index, setIndex] = useState(0);
  const { getConfigCloud, updateConfig, config } = useConfig();
  const [dualView, setDualView] = useState(window.innerWidth >= 750);
  const [error, setError] = useState(null);
  const [showFirstTime, setShowFirstTime] = useState(false);
  const [isCurrentMyContentEmpty, setIsCurrentMyContentEmpty] = useState(false);
  const navigate = useNavigate();
  const breakpoint = 750;

  const ctx = useAutoPlay();



  const handleSwipeLeft = () => {
    if (!dualView) setIndex(1); // Switch to practice
  };

  const handleSwipeRight = () => {
    if (!dualView) setIndex(0); // Switch to content
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleSwipeLeft,
    onSwipedRight: handleSwipeRight,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const toggleMode = () => {
    setIndex(prev => (prev === 0 ? 1 : 0));
  };

  const sentenceCallBack = (sObj, source) => {
    console.log('SENTENCE: ', sObj, 'SOURCE: ', source);
    setSentence(sObj);
    if (source) {
      setSentenceSource(source);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setDualView(window.innerWidth >= breakpoint);
    };

    const initializeConfig = async () => {
      if (isEmpty(config)) {
        try {
          const cfg = await getConfigCloud();
          if (cfg?.firstTime?.mainPage) {
            setShowFirstTime(true);
          }
        } catch (error) {
          setError(error);
        }
      }
      setLoading(false);
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    initializeConfig();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line
  }, [error]);

  const handleFirstTimeClose = () => {
    setShowFirstTime(false);
    const ft = config['firstTime'];
    ft['mainPage'] = false;
    updateConfig('firstTime', ft);
  };

  const navigateLogin = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflowY: 'hidden',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', px: 1 }}>
      {/* <Header /> */}
       <Header dualView={dualView} 
               index={index} 
               toggleMode = {toggleMode}
      />
      {showFirstTime && (
        <FirstTimeLangSelection open={showFirstTime} onClose={handleFirstTimeClose} />
      )}
      {!error ? (
        <Box sx={{ flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  pt: 0, 
                  my: 0,
                  overflowY: 'hidden',
                  overflowX: 'hidden' }}>

          <Box
            {...(!dualView ? swipeHandlers : {})}
            sx={{
              flexGrow: 1,
              display: 'flex',
              width: dualView ? '100%' : '200%',
              transform: `translateX(${dualView ? 0 : index * -51.5}%)`,
              transition: 'transform 0.5s ease-in-out',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <Box sx={{ flex: '1 0 50%',
               width: '100%', 
              //  overflowY: 'auto' ,
               overflowY: 'hidden' ,
               my:0
               }}>
              <ContentPanel
                sentenceCallBack={sentenceCallBack}
                sentenceValue={sentence}
                errorCallBack={(err) => setError(err)}
                myContentEmptyCallBack={setIsCurrentMyContentEmpty}
              />
            </Box>
            <Box
              sx={{
                flex: '1 0 50%',
                width: '100%',
                overflowY: 'auto',
                ml: 1,
                mr: 1,
              }}
            >
              <VoiceRecorder
                sentence={sentence}
                sentenceSource={sentenceSource}
                sentenceCallBack={sentenceCallBack}
                // handleAutoPlayEvent={handleAutoPlayEvent}
                toggleMode={toggleMode}
                dualView={dualView}
                isCurrentMyContentEmpty={isCurrentMyContentEmpty}
                errorCallBack={(err) => setError(err)}
              />
      
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1 }}>
          {error && (
            <ErrorDispatcher
              error={error}
              callBackMapClose={{
                '4030': navigateLogin,
                '5020': navigateLogin,
                '4034': navigateLogin,
                '4999': navigateLogin,
              }}
              resetError={() => {
                console.log('Resetting error in MainContent');
                setSentence(new Sentence());
                setError(null);
              }}
              showDialog={false}
              navigateToLogin={true}
            />
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1     }} />
      <Footer />
    </Box>
  );
};

export default MainContent;
// This code is a React component for the main content area of a language learning application.
// It includes features like content display, voice recording, and responsive design for different screen sizes.
// The component uses hooks for state management and side effects, and it integrates with a configuration provider.
// It also handles errors and provides a first-time user experience for language selection.
// The component is designed to be responsive, adapting to different screen sizes and orientations.
// It uses Material-UI for styling and layout, and it supports swipe gestures for navigation on mobile devices.
// The component is structured to allow for easy expansion and modification, making it suitable for a dynamic learning platform.
// The code is modular, with separate components for content display and voice recording, promoting reusability and maintainability.
/// The component is designed to be part of a larger application, integrating with authentication and configuration systems.
// It is optimized for performance, with lazy loading and efficient state management.
